<template>
  <div class="my-1 py-25">
    <div class="d-flex justify-content-between align-items-center mb-1">
      <div>
        <b-button variant="outline-primary" size="sm" class="mr-50" @click="showSourcesModal = true">
          <feather-icon icon="DownloadCloudIcon" class="mr-50" />
          <span>Подобрать из источников</span>
        </b-button>
        <b-button variant="outline-secondary" size="sm" @click="showCreateModal = true">
          <feather-icon icon="PlusIcon" class="mr-50" />
          <span>Добавить вручную</span>
        </b-button>
      </div>
      <div class="text-muted font-small-3">
        Всего: {{ soundtracks.length }}
      </div>
    </div>

    <b-overlay :show="isLoading" rounded="sm">
      <div v-if="loaded && !soundtracks.length" class="text-muted">
        Саундтреков нет — попробуйте «Подобрать из источников»
      </div>

      <b-table
          v-if="soundtracks.length"
          :items="soundtracks"
          :fields="tableColumns"
          primary-key="id"
          responsive
          small
          striped
          show-empty
          empty-text="Саундтреков нет"
          class="position-relative"
      >
        <template #cell(title)="data">
          <div class="d-flex align-items-center">
            <b-button
                v-if="playableUrl(data.item)"
                v-b-tooltip.hover
                :title="data.item.file_url ? 'Полный файл' : 'Превью 30 сек'"
                :variant="data.item.file_url ? 'flat-success' : 'flat-secondary'"
                class="btn-icon p-25 mr-50"
                size="sm"
                @click="playSoundtrack(data.item)"
            >
              <feather-icon :icon="isTrackPlaying(`soundtrack-${data.item.id}`) ? 'PauseIcon' : 'PlayIcon'" size="14" />
            </b-button>
            <div class="soundtrack-title-cell">
              <div class="text-truncate">
                <feather-icon
                    v-b-tooltip.hover
                    :title="kindTooltip(data.item)"
                    :icon="data.item.kind === 'licensed' ? 'FilmIcon' : 'DiscIcon'"
                    :class="data.item.kind === 'licensed' ? 'text-primary' : 'text-secondary'"
                    size="13"
                    class="mr-25 align-middle"
                />
                <span v-if="data.item.track_number" class="text-muted mr-25">{{ data.item.track_number }}.</span>
                {{ data.item.title }}
              </div>
              <div class="font-small-2 text-muted text-nowrap">
                <span class="mr-50">{{ data.item.artist_name || '—' }}</span>
                <b-badge
                    v-if="data.item.spotify_id"
                    tag="a"
                    :href="`https://open.spotify.com/track/${data.item.spotify_id}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="success"
                    class="mr-25 cursor-pointer soundtrack-mini-badge"
                >SP</b-badge>
                <b-badge
                    v-if="data.item.source_data && data.item.source_data.deezer_id"
                    variant="light-secondary"
                    class="mr-25 soundtrack-mini-badge"
                >DZ</b-badge>
                <b-badge
                    v-if="data.item.source_data && data.item.source_data.apple_id"
                    variant="light-secondary"
                    class="soundtrack-mini-badge"
                >AP</b-badge>
              </div>
            </div>
          </div>
        </template>

        <template #cell(timecode)="data">
          <span v-if="data.item.timecode !== null || data.item.scene_description" class="text-nowrap">
            <span v-if="data.item.timecode !== null" class="mr-25">{{ formatTimecode(data.item.timecode) }}</span>
            <feather-icon
                v-if="data.item.scene_description"
                v-b-tooltip.hover
                :title="data.item.scene_description"
                icon="InfoIcon"
                size="14"
                class="text-muted align-middle cursor-pointer"
            />
          </span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(track)="data">
          <b-link
              v-if="data.item.track"
              v-b-tooltip.hover
              :title="data.item.track.title"
              :to="{ name: 'track-detail', params: { id: data.item.track.id } }"
              target="_blank"
              class="d-block text-truncate soundtrack-track-cell"
          >
            {{ data.item.track.title }}
          </b-link>
          <b-button
              v-else
              variant="flat-warning"
              size="sm"
              class="btn-icon"
              title="Попробовать сматчить"
              @click="matchSoundtrack(data.item)"
          >
            <feather-icon icon="SearchIcon" size="14" />
          </b-button>
        </template>

        <template #cell(file)="data">
          <feather-icon
              v-b-tooltip.hover
              :title="fileOwnerLabels[data.item.file_owner]"
              :icon="fileOwnerIcons[data.item.file_owner]"
              :class="fileOwnerClasses[data.item.file_owner]"
              size="16"
              class="mr-50 align-middle"
          />
          <b-button
              variant="flat-primary"
              size="sm"
              class="btn-icon"
              title="Скачать файл"
              @click="openDownloadModal(data.item)"
          >
            <feather-icon icon="DownloadIcon" size="14" />
          </b-button>
          <b-button
              v-if="data.item.file_owner === 'own'"
              variant="flat-danger"
              size="sm"
              class="btn-icon"
              title="Удалить файл"
              @click="removeFile(data.item)"
          >
            <feather-icon icon="XIcon" size="14" />
          </b-button>
        </template>

        <template #cell(actions)="data">
          <b-button variant="flat-danger" size="sm" class="btn-icon" @click="removeSoundtrack(data.item)">
            <feather-icon icon="TrashIcon" size="14" />
          </b-button>
        </template>
      </b-table>
    </b-overlay>

    <movie-soundtrack-sources-modal
        v-model="showSourcesModal"
        :movie-id="movieId"
        @updated="fetchSoundtracks(true)"
    />

    <movie-soundtrack-download-modal
        v-model="showDownloadModal"
        :movie-id="movieId"
        :soundtrack="downloadTarget"
        @updated="fetchSoundtracks(true)"
    />

    <b-modal
        v-model="showCreateModal"
        title="Добавить саундтрек вручную"
        centered
        ok-title="Добавить"
        cancel-title="Отмена"
        :ok-disabled="!newSoundtrack.title"
        @ok="createSoundtrack"
    >
      <b-form-group label="Название">
        <b-form-input v-model="newSoundtrack.title" placeholder="Название трека" />
      </b-form-group>
      <b-form-group label="Исполнитель">
        <b-form-input v-model="newSoundtrack.artist_name" placeholder="Исполнитель" />
      </b-form-group>
      <b-form-group label="Тип">
        <b-form-select v-model="newSoundtrack.kind" :options="kindOptions" />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton, BBadge, BTable, BOverlay, BLink, BModal, BFormGroup, BFormInput, BFormSelect, VBTooltip,
} from 'bootstrap-vue'
import { ref } from '@vue/composition-api'
import { toggleTrack, isTrackPlaying } from '@/layouts/components/globalPlayer'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import MovieSoundtrackSourcesModal from './MovieSoundtrackSourcesModal.vue'
import MovieSoundtrackDownloadModal from './MovieSoundtrackDownloadModal.vue'

export default {
  components: {
    BButton,
    BBadge,
    BTable,
    BOverlay,
    BLink,
    BModal,
    BFormGroup,
    BFormInput,
    BFormSelect,
    MovieSoundtrackSourcesModal,
    MovieSoundtrackDownloadModal,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    movieId: { type: [String, Number], required: true },
    movieTitle: { type: String, default: '' },
  },
  setup(props, { emit }) {
    const toast = useToast()

    const soundtracks = ref([])
    const isLoading = ref(false)
    const loaded = ref(false)
    const showSourcesModal = ref(false)
    const showDownloadModal = ref(false)
    const showCreateModal = ref(false)
    const downloadTarget = ref(null)
    const newSoundtrack = ref({ title: '', artist_name: '', kind: 'licensed' })

    const kindOptions = [
      { value: 'licensed', text: 'Звучит в сцене' },
      { value: 'score', text: 'Трек альбома' },
    ]

    // Тип, исполнитель и внешние ссылки живут второй строкой в колонке «Трек»,
    // а длинные тексты обрезаются с подсказкой — иначе таблица уезжает в прокрутку
    const tableColumns = [
      { key: 'title', label: 'Трек' },
      { key: 'timecode', label: 'Сцена' },
      { key: 'track', label: 'Трек в БД' },
      { key: 'file', label: 'Файл', tdClass: 'text-nowrap' },
      { key: 'actions', label: '', thStyle: 'width: 40px' },
    ]

    const fileOwnerLabels = {
      own: 'Свой файл',
      track: 'Файл связанного трека',
      none: 'Файла нет',
    }

    const fileOwnerIcons = {
      own: 'CheckCircleIcon',
      track: 'LinkIcon',
      none: 'MinusCircleIcon',
    }

    const fileOwnerClasses = {
      own: 'text-success',
      track: 'text-info',
      none: 'text-muted',
    }

    const notifyError = (error, fallback) => {
      toast({
        component: ToastificationContent,
        props: {
          title: (error.response && error.response.data && error.response.data.message) || fallback,
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    }

    const notifyOk = title => {
      toast({
        component: ToastificationContent,
        props: { title, icon: 'CheckIcon', variant: 'success' },
      })
    }

    const fetchSoundtracks = (force = false) => {
      if (loaded.value && !force) return

      isLoading.value = true
      store
        .dispatch('movies/fetchMovieSoundtracks', { id: props.movieId })
        .then(response => {
          soundtracks.value = response.data.data
          loaded.value = true
          emit('updated', soundtracks.value.length)
        })
        .catch(error => notifyError(error, 'Не удалось загрузить саундтреки'))
        .finally(() => { isLoading.value = false })
    }

    const formatTimecode = seconds => {
      const minutes = Math.floor(seconds / 60)
      const rest = seconds % 60

      return `${minutes}:${String(rest).padStart(2, '0')}`
    }

    // Полный файл (свой либо связанного трека) важнее 30-секундного превью источника
    const playableUrl = item => item.file_url || item.preview_url || null

    const kindTooltip = item => {
      if (item.kind === 'licensed') return 'Звучит в сцене'

      return item.album_title ? `Трек альбома: ${item.album_title}` : 'Трек альбома'
    }

    const soundtrackSourceLabels = {
      own: 'Свой файл',
      track: 'Файл трека из базы',
    }

    // Саундтрек уходит в общий плеер шапки; контекст — фильм, таймкод, сцена
    const playSoundtrack = item => {
      toggleTrack({
        key: `soundtrack-${item.id}`,
        url: playableUrl(item),
        title: item.title,
        artist: item.artist_name,
        sourceLabel: soundtrackSourceLabels[item.file_owner] || 'Превью 30 сек',
        link: { name: 'movie-view', params: { id: props.movieId } },
        details: [
          props.movieTitle ? `«${props.movieTitle}»` : null,
          item.timecode !== null ? formatTimecode(item.timecode) : null,
        ],
        hint: item.scene_description || null,
      })
    }

    const matchSoundtrack = item => {
      store
        .dispatch('movies/matchMovieSoundtrack', { id: props.movieId, soundtrackId: item.id })
        .then(response => {
          const updated = response.data.data
          if (updated.track) notifyOk('Трек найден и привязан')
          else notifyOk('Подходящий трек не найден')
          fetchSoundtracks(true)
        })
        .catch(error => notifyError(error, 'Не удалось выполнить поиск трека'))
    }

    const openDownloadModal = item => {
      downloadTarget.value = item
      showDownloadModal.value = true
    }

    const removeFile = item => {
      if (!window.confirm(`Удалить файл «${item.title}»?`)) return

      store
        .dispatch('movies/deleteMovieSoundtrackFile', { id: props.movieId, soundtrackId: item.id })
        .then(() => {
          notifyOk('Файл удалён')
          fetchSoundtracks(true)
        })
        .catch(error => notifyError(error, 'Не удалось удалить файл'))
    }

    const removeSoundtrack = item => {
      if (!window.confirm(`Удалить саундтрек «${item.title}»?`)) return

      store
        .dispatch('movies/deleteMovieSoundtrack', { id: props.movieId, soundtrackId: item.id })
        .then(() => {
          notifyOk('Саундтрек удалён')
          fetchSoundtracks(true)
        })
        .catch(error => notifyError(error, 'Не удалось удалить саундтрек'))
    }

    const createSoundtrack = () => {
      store
        .dispatch('movies/createMovieSoundtrack', { id: props.movieId, payload: newSoundtrack.value })
        .then(() => {
          notifyOk('Саундтрек добавлен')
          newSoundtrack.value = { title: '', artist_name: '', kind: 'licensed' }
          fetchSoundtracks(true)
        })
        .catch(error => notifyError(error, 'Не удалось добавить саундтрек'))
    }

    fetchSoundtracks()

    return {
      soundtracks,
      isLoading,
      loaded,
      showSourcesModal,
      showDownloadModal,
      showCreateModal,
      downloadTarget,
      newSoundtrack,
      kindOptions,
      tableColumns,
      fileOwnerLabels,
      fileOwnerIcons,
      fileOwnerClasses,
      fetchSoundtracks,
      formatTimecode,
      playableUrl,
      kindTooltip,
      playSoundtrack,
      isTrackPlaying,
      matchSoundtrack,
      openDownloadModal,
      removeFile,
      removeSoundtrack,
      createSoundtrack,
    }
  },
}
</script>

<style lang="scss" scoped>
// text-truncate внутри ячейки таблицы работает только при ограниченной ширине
.soundtrack-title-cell {
  min-width: 0;
  max-width: 360px;
}

.soundtrack-track-cell {
  max-width: 220px;
}

.soundtrack-mini-badge {
  font-size: 0.65rem;
}
</style>
